import React, { Suspense } from 'react';
import SuspenseLoader from '../components/suspenseLoader/SuspenseLoader';

/**
 * Wraps a lazy-loaded component in a Suspense boundary with a loading fallback.
 * This centralizes Suspense handling for all lazy-loaded routes.
 *
 * @param Component - A lazy-loaded React component (from React.lazy())
 * @returns JSX element wrapped in Suspense
 *
 * @example
 * ```tsx
 * const AdminPage = React.lazy(() => import('./admin'));
 * // In router:
 * element: withSuspense(AdminPage)
 * ```
 */
export const withSuspense = <T extends React.ComponentType<Record<string, never>>>(
  Component: React.LazyExoticComponent<T>
): React.ReactElement => {
  // Type assertion needed because route components typically don't receive props
  // (they use hooks like useParams() instead)
  const ComponentWithProps = Component as React.LazyExoticComponent<React.ComponentType<Record<string, never>>>;
  return (
    <Suspense fallback={<SuspenseLoader />}>
      <ComponentWithProps />
    </Suspense>
  );
};
